
import Vue from "vue";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import Headline from "../components/shared/Headline.vue";
import Breadcrumb from "../components/shared/Breadcrumb.vue";
import BreadcrumbItem from "../components/shared/BreadcrumbItem.vue";
import Modal from "../components/shared/Modal.vue";
import SelectSongs from "../components/SelectSongs.vue";
import CompareSongs from "../components/CompareSongs.vue";
import {
  IUser,
  ISong,
  ISongWithLive,
  ISongWithRank,
  IUserSongsHistory,
} from "../@types/interface.d";
import MultiMovies from "../components/shared/MultiMovies.vue";
import UserAPI from "../api/UserAPI";
import seedByRanking from "../utils/seedByRanking";

dayjs.extend(utc);

interface DataType {
  user: IUser | undefined;
  isMine: boolean;
  isEditing: boolean;
  notFound: boolean;
  selected: number;
  nameModal: boolean;
  nameInModal: string;
  descModal: boolean;
  songs: ISong[];
  editingSongs: ISong[] | undefined;
  showingSongs: ISong[];
  showingDate?: string;
  histories: IUserSongsHistory;
}

export default Vue.extend({
  name: "myPage",
  components: {
    Headline,
    Breadcrumb,
    BreadcrumbItem,
    Modal,
    SelectSongs,
    CompareSongs,
    MultiMovies,
  },
  data(): DataType {
    return {
      user: undefined,
      notFound: false,
      isEditing: false,
      selected: 1,
      isMine: false,
      nameModal: false,
      nameInModal: "",
      descModal: false,
      songs: [],
      editingSongs: undefined,
      showingSongs: undefined,
      showingDate: undefined,
      histories: [],
    };
  },
  created() {
    this.init();
  },
  beforeRouteUpdate() {
    this.init();
  },
  methods: {
    async init() {
      const { userId } = this.$route.params;
      const storedId = localStorage.getItem("userId");
      const token = localStorage.getItem("userToken");

      await fetch("/api/songs")
        .then(res => res.json())
        .then((data: ISongWithLive[]) => {
          this.songs = data.map(v => v.song);
        });

      try {
        const user = await (userId === storedId && token
          ? UserAPI.verify(userId, token).then(v => {
            this.isMine = true;
            return v;
          }).catch(() => this.getUser(userId))
          : UserAPI.get(userId));
        this.user = user;
        this.nameInModal = user.name || "";
        if (user.favoriteSongs.length === 0) {
          if (user.coolSongs.length > 0) this.selected = 2;
          if (user.prettySongs.length > 0) this.selected = 3;
          if (user.funSongs.length > 0) this.selected = 4;
          if (user.emotionalSongs.length > 0) this.selected = 5;
        }
        this.histories = await UserAPI.histories(userId);
        this.showingSongs = this.kindSongs(this.selected);
        this.showingDate = this.kindDate(this.selected);
      } catch {
        this.notFound = true;
      }
    },
    badgeClick(n: number) {
      this.selected = n;
      this.showingSongs = this.selectedSongs;
      this.showingDate = this.selectedDate;
    },
    isSelected(n: number): string {
      return this.selected === n ? "badge-primary" : "";
    },
    modifyName() {
      this.nameModal = true;
    },
    editSongs() {
      this.isEditing = true;
    },
    updateName(bUpdate: boolean) {
      this.nameModal = false;
      if (this.user && bUpdate) {
        this.user.name = this.nameInModal;
        this.updateUser();
      }
    },
    openDesc() {
      this.descModal = true;
    },
    closeDesc() {
      this.descModal = false;
    },
    async updateUser() {
      const token = localStorage.getItem("userToken");
      await UserAPI.update(this.user, token);
      this.showingSongs = this.selectedSongs;
      this.showingDate = this.selectedDate;

      setTimeout(async () => {
        this.histories = await UserAPI.histories(this.user.id);
      }, 200);
    },
    cancel() {
      this.isEditing = false;
    },
    async selectCandidates(songIds: number[]) {
      const songs = songIds.map(id => this.songs.find(v => v.id === id));
      this.editingSongs = await this.seedSongs(songs);
    },
    // 全体ランキングの順に並べ替える（シード）。取得できなければランダム順。
    async seedSongs(songs: ISong[]): Promise<ISong[]> {
      let ranking: ISongWithRank[] = [];
      try {
        ranking = await fetch("/api/ranking").then(res => res.json());
      } catch (e) {
        console.log("Failed to load ranking. Songs are not seeded.", e);
      }
      return seedByRanking(songs, ranking, String(this.selected));
    },
    finish(songs: ISong[]) {
      const songIds = songs.map(v => v.id);
      switch (this.selected) {
        case 1:
          this.user.favoriteSongs = songIds;
          this.user.favoriteUpdatedAt = dayjs.utc().format();
          break;
        case 2:
          this.user.coolSongs = songIds;
          this.user.coolUpdatedAt = dayjs.utc().format();
          break;
        case 3:
          this.user.prettySongs = songIds;
          this.user.prettyUpdatedAt = dayjs.utc().format();
          break;
        case 4:
          this.user.funSongs = songIds;
          this.user.funUpdatedAt = dayjs.utc().format();
          break;
        case 5:
          this.user.emotionalSongs = songIds;
          this.user.emotionalUpdatedAt = dayjs.utc().format();
          break;
        default:
          break;
      }
      this.updateUser(this.selected);
      this.isEditing = false;
      this.editingSongs = undefined;
    },
    twitterLink(): string {
      const songId = this.user.favoriteSongs[0]
        || this.user.coolSongs[0]
        || this.user.prettySongs[0]
        || this.user.funSongs[0]
        || this.user.emotionalSongs[0];
      const song = this.songs.find(v => v.id === songId);
      const url = song
        ? "https://twitter.com/intent/tweet?"
          + `url=${location.href}`
          + `&text=いぎなり東北産で私の一番好きな曲は「${song.name}」です\n`
          + "&hashtags=いぎなり東北産"
        : "https://twitter.com/intent/tweet?"
          + `url=${location.href}`
          + "&text=いぎなり東北産で私の好きな楽曲です\n"
          + "&hashtags=いぎなり東北産";

      return url;
    },
    formatDate(d: string): string {
      if (!d) {
        return "";
      }
      return dayjs(d).format("YYYY/MM/DD HH:mm");
    },
    selectHistory(historyId: number) {
      if (historyId === 0) {
        this.showingSongs = this.selectedSongs;
        this.showingDate = this.selectedDate;
        return;
      }
      const history = this.histories.find(v => v.id === historyId);
      if (!history) {
        return;
      }
      this.showingSongs = history.songs.map(key => this.songs.find(v => v.id === key) as ISong);
      this.showingDate = this.formatDate(history.originalCreatedAt);
    },
    kindSongs(kind: number): ISong[] {
      if (!this.user || this.songs.length === 0) {
        return [];
      }
      let songs: number[] = [];
      switch (kind) {
        case 1: songs = this.user.favoriteSongs; break;
        case 2: songs = this.user.coolSongs; break;
        case 3: songs = this.user.prettySongs; break;
        case 4: songs = this.user.funSongs; break;
        case 5: songs = this.user.emotionalSongs; break;
        default: break;
      }
      const result = songs.map((key: number) => this.songs.find(v => v.id === key) as ISong);
      return result;
    },
    kindDate(kind: number): string {
      if (!this.user) {
        return "";
      }
      let d = "";
      switch (kind) {
        case 1: d = this.user.favoriteUpdatedAt; break;
        case 2: d = this.user.coolUpdatedAt; break;
        case 3: d = this.user.prettyUpdatedAt; break;
        case 4: d = this.user.funUpdatedAt; break;
        case 5: d = this.user.emotionalUpdatedAt; break;
        default: break;
      }
      return this.formatDate(d);
    },
  },
  computed: {
    selectedSongs(): ISong[] {
      return this.kindSongs(this.selected);
    },
    selectedDate(): string {
      return this.kindDate(this.selected);
    },
    selectedTitle(): string {
      switch (this.selected) {
        case 1: return "好きな曲";
        case 2: return "かっこいい曲";
        case 3: return "かわいい曲";
        case 4: return "楽しい曲";
        case 5: return "エモい曲";
        default: return "";
      }
    },
    selectedTitle2(): string {
      switch (this.selected) {
        case 1: return "好き";
        case 2: return "かっこいい";
        case 3: return "かわいい";
        case 4: return "楽しい";
        case 5: return "エモい";
        default: return "";
      }
    },
    showTwitterShare(): boolean {
      const user = this.user;
      if (!user) {
        return false;
      }
      return this.isMine && !this.isEditing
        && (user.favoriteSongs.length > 0
          || user.coolSongs.length > 0
          || user.prettySongs.length > 0
          || user.funSongs.length > 0
          || user.emotionalSongs.length > 0
        );
    },
    currentHistories(): IUserSongsHistory[] {
      return this.histories.filter(v => v.songListKind === this.selected);
    },
    movies(): string[] {
      const user = this.user;
      if (this.songs.length === 0) {
        return [];
      }
      if (!user || !user.favoriteSongs || user.favoriteSongs.length === 0) {
        return [];
      }
      const songId = user.favoriteSongs[0];
      const song = this.songs.find(v => v.id === songId);
      if (!song) {
        return [];
      }
      return song.movie;
    },
  },
});

